/**
 * client/net/clock.ts — server time estimation, Q3 CL_AdjustTimeDelta port [NET §7.1].
 * OWNER: NETCODE agent.
 *
 * Q3 (client/cl_cgame.c):
 *   on each new snapshot:  newDelta = snap.serverTime - realtime
 *     |newDelta - delta| > 500 (RESET_TIME) -> delta = newDelta
 *     |newDelta - delta| > 100              -> delta = (delta + newDelta) / 2   ("fast adjust")
 *     else                                  -> delta += extrapolated ? -2 : +1   (slow drift)
 *   each frame:  serverTime = realtime + delta - timeNudge (never backwards);
 *                extrapolated |= realtime + delta >= latestSnap.serverTime - 5   (no nudge here)
 *
 * START OFFSET (ESTIMATE, ARCHITECTURE §9.7): Q3 CL_FirstSnapshot sets delta so render time equals
 * the newest snapshot, then the -2 ms drift pulls it back over the first second while every frame
 * extrapolates. We start one snapshot interval (50 ms) behind instead, which is where the drift
 * settles anyway at 20 Hz, so remote players interpolate from the first frame. The offset is folded
 * into newDelta, so the reset/fast/slow thresholds stay centred on the steady state.
 *
 * WARM-UP (ESTIMATE, netcode fix): the first snapshot often arrives late (the page is still busy
 * finishing the load), and Q3 then needs +1 ms per snapshot to recover: measured renderLag 33-130 ms
 * for the first ~5 s instead of ~20 ms. During the first CLOCK_WARMUP_SNAPS snapshots, a snapshot
 * that arrives more than CLOCK_WARMUP_MIN_JUMP_MS "earlier" than the estimate (and no frame got near
 * the edge) moves delta forward to it at once: the least-delayed snapshot is the best estimate of
 * the true server clock. After that the plain Q3 rules apply.
 */
import {
  CLOCK_DRIFT_DOWN, CLOCK_DRIFT_UP, CLOCK_FAST_MS, CLOCK_RESET_MS, SERVER_TICK_MS, TIME_NUDGE_MAX, TIME_NUDGE_MIN,
} from '@shared/constants';

/** Start offset behind the newest snapshot (ESTIMATE, see header). */
export const CLOCK_START_OFFSET_MS = SERVER_TICK_MS;
/** Warm-up length in snapshots (1 s at 20 Hz, ESTIMATE) and the minimum forward jump (ESTIMATE). */
export const CLOCK_WARMUP_SNAPS = 20;
export const CLOCK_WARMUP_MIN_JUMP_MS = 5;

export class ServerClock {
  /** serverTime - realTime (ms), Q3 cl.serverTimeDelta. */
  delta = 0;
  private init = false;
  /** Q3 cl.extrapolatedSnapshot: a frame got within 5 ms of the newest snapshot. */
  private nearEdge = false;
  private lastRender = -Infinity;
  /** Frames that rendered BEYOND the newest snapshot (true extrapolation), for NetStats. */
  extrapolations = 0;
  resets = 0;
  /** Snapshots since the last (re)initialisation, for the warm-up. */
  private snapsSinceInit = 0;

  onSnapshot(serverTime: number, realNow: number): void {
    const newDelta = serverTime - realNow - CLOCK_START_OFFSET_MS;
    const dd = Math.abs(newDelta - this.delta);
    if (!this.init || dd > CLOCK_RESET_MS) {
      this.delta = newDelta;
      if (this.init) this.resets++;
      this.init = true;
      this.snapsSinceInit = 0;
      this.lastRender = -Infinity; // a reset may legitimately go backwards (reconnect, new match)
    } else if (dd > CLOCK_FAST_MS) this.delta = (this.delta + newDelta) / 2;
    else if (this.snapsSinceInit < CLOCK_WARMUP_SNAPS && !this.nearEdge && newDelta > this.delta + CLOCK_WARMUP_MIN_JUMP_MS) this.delta = newDelta;
    else this.delta += this.nearEdge ? -CLOCK_DRIFT_DOWN : CLOCK_DRIFT_UP;
    this.snapsSinceInit++;
    this.nearEdge = false;
  }

  /** Render time for this frame (monotonic). Includes the time nudge (that is what viewTime must be). */
  renderTime(realNow: number, timeNudge: number, latestSnapTime: number): number {
    const nudge = Math.min(TIME_NUDGE_MAX, Math.max(TIME_NUDGE_MIN, timeNudge || 0));
    const raw = realNow + this.delta;
    let t = raw - nudge;
    if (t < this.lastRender) t = this.lastRender;
    this.lastRender = t;
    if (raw >= latestSnapTime - 5) this.nearEdge = true; // Q3 "-5", measured without the nudge
    if (t > latestSnapTime) this.extrapolations++;
    return t;
  }

  get ready(): boolean { return this.init; }
}
